import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { storeDashboardAPI, storeAlertsAPI, storeRestockAPI } from '../../services/storeAPI';
import StoreHeader from '../../components/Layout/StoreHeader';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import LiveIndicator from '../../components/LiveIndicator';

interface DashboardData {
  summary: {
    total_inventory_items: number;
    low_stock_count: number;
    total_bulk_bins: number;
    bins_needing_refill: number;
    unresolved_alerts: number;
  };
  low_stock_items: Array<{
    id: number;
    product_name: string;
    quantity: number;
    reorder_threshold: number;
    location: string;
  }>;
  bins_needing_refill: Array<{
    id: number;
    name: string;
    product_name: string;
    percentage_full: number;
    current_lbs: number;
  }>;
  recent_transactions: Array<{
    id: number;
    type: string;
    type_display: string;
    product_name: string;
    quantity: number;
    quantity_lbs: number | null;
    user: string;
    timestamp: string;
  }>;
}

interface Alert {
  id: number;
  alert_type: string;
  alert_type_display: string;
  priority: string;
  priority_display: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface LowStockItem {
  id: number;
  product_id: number;
  product_name: string;
  current_quantity: number;
  reorder_threshold: number;
  reorder_quantity: number;
  deficit: number;
}

const StoreDashboard: React.FC = () => {
  const { user, isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<{ [id: number]: number }>({});
  const [restockLoading, setRestockLoading] = useState(false);
  const [restockSuccess, setRestockSuccess] = useState('');
  const [millRequestCount, setMillRequestCount] = useState(0);

  const loadDashboard = useCallback(async () => {
    try {
      if (!dashboard) setLoading(true);
      const [dashboardData, alertsData] = await Promise.all([
        storeDashboardAPI.getDashboard(),
        storeAlertsAPI.getAll(),
      ]);
      setDashboard(dashboardData);
      setAlerts(alertsData);
      setError('');

      // Check mill request count
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const res = await fetch(`${API_BASE}/store/mill-requests/count/`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          setMillRequestCount(data.count || 0);
        }
      } catch {
        // Silently ignore
      }
    } catch (err: any) {
      if (!dashboard) setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [dashboard]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadDashboard();
  }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 30 seconds (smart - respects user activity)
  const { isRefreshing, secondsSinceUpdate, isPaused, pauseReason } = useAutoRefresh(loadDashboard, {
    intervalMs: 30000,
    enabled: isAuthenticated && !loading && !error,
  });

  const handleResolveAlert = async (alertId: number) => {
    try {
      await storeAlertsAPI.resolve(alertId);
      setAlerts(alerts.filter(a => a.id !== alertId));
      if (dashboard) {
        setDashboard({
          ...dashboard,
          summary: {
            ...dashboard.summary,
            unresolved_alerts: dashboard.summary.unresolved_alerts - 1,
          },
        });
      }
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const openRestockModal = async () => {
    try {
      const response = await storeRestockAPI.getLowStockItems();
      setLowStockItems(response.items || []);
      // Pre-select all items with their suggested reorder quantities
      const selections: { [id: number]: number } = {};
      (response.items || []).forEach((item: LowStockItem) => {
        selections[item.id] = item.reorder_quantity;
      });
      setSelectedItems(selections);
      setShowRestockModal(true);
      setRestockSuccess('');
    } catch (err) {
      console.error('Failed to load low stock items:', err);
    }
  };

  const toggleItem = (id: number, quantity: number) => {
    if (selectedItems[id] !== undefined) {
      const newItems = { ...selectedItems };
      delete newItems[id];
      setSelectedItems(newItems);
    } else {
      setSelectedItems({ ...selectedItems, [id]: quantity });
    }
  };

  const updateQuantity = (id: number, quantity: number) => {
    setSelectedItems({ ...selectedItems, [id]: quantity });
  };

  const submitRestockRequest = async () => {
    const items = Object.entries(selectedItems).map(([id, quantity]) => ({
      inventory_id: parseInt(id),
      quantity,
    }));

    if (items.length === 0) {
      alert('Please select at least one item to restock');
      return;
    }

    try {
      setRestockLoading(true);
      await storeRestockAPI.createRestockRequest(items);
      setRestockSuccess(`Restock request sent for ${items.length} item(s). The mill team will be notified.`);
      setSelectedItems({});
      // Reload dashboard to update alerts
      loadDashboard();
    } catch (err: any) {
      alert(err.message || 'Failed to send restock request');
    } finally {
      setRestockLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      <main style={styles.main}>
        <div style={styles.welcomeSection}>
          <div>
            <h1 style={styles.welcome}>
              Welcome, {user?.first_name || user?.username}!
            </h1>
            <p style={styles.date}>{new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}</p>
          </div>
          <LiveIndicator
            isRefreshing={isRefreshing}
            secondsSinceUpdate={secondsSinceUpdate}
            isPaused={isPaused}
            pauseReason={pauseReason}
          />
        </div>

        {loading ? (
          <div style={styles.loading}>Loading dashboard...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : dashboard ? (
          <>
            {/* Summary Cards */}
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard} onClick={() => navigate('/store/inventory')}>
                <div style={styles.cardIcon}>📦</div>
                <div style={styles.cardValue}>{dashboard.summary.total_inventory_items}</div>
                <div style={styles.cardLabel}>Inventory Items</div>
              </div>

              <div style={{...styles.summaryCard, ...styles.alertCard}} onClick={() => navigate('/store/inventory')}>
                <div style={styles.cardIcon}>⚠️</div>
                <div style={styles.cardValue}>{dashboard.summary.low_stock_count}</div>
                <div style={styles.cardLabel}>Low Stock</div>
              </div>

              <div style={styles.summaryCard} onClick={() => navigate('/store/bins')}>
                <div style={styles.cardIcon}>🗑️</div>
                <div style={styles.cardValue}>{dashboard.summary.total_bulk_bins}</div>
                <div style={styles.cardLabel}>Bulk Bins</div>
              </div>

              <div style={{...styles.summaryCard, ...(dashboard.summary.bins_needing_refill > 0 ? styles.alertCard : {})}}
                   onClick={() => navigate('/store/bins')}>
                <div style={styles.cardIcon}>🔄</div>
                <div style={styles.cardValue}>{dashboard.summary.bins_needing_refill}</div>
                <div style={styles.cardLabel}>Bins Need Refill</div>
              </div>
            </div>

            {/* Mill Requests Notification */}
            {millRequestCount > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #e8eaf6, #ede7f6)',
                border: '2px solid #7c3aed',
                borderRadius: '12px', padding: '16px 20px', marginBottom: '1.5rem',
                cursor: 'pointer',
              }}
                onClick={() => navigate('/store/orders?tab=mill_requests')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>🏭</span>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '16px', color: '#4a148c' }}>
                      Mill Requests: {millRequestCount} item{millRequestCount !== 1 ? 's' : ''} to pull
                    </div>
                    <div style={{ fontSize: '13px', color: '#7c3aed' }}>
                      Items needed for shipping orders — tap to view
                    </div>
                  </div>
                </div>
                <button style={{
                  padding: '8px 16px', background: '#7c3aed', color: '#fff',
                  border: 'none', borderRadius: '8px', fontWeight: '600',
                  fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  View Requests
                </button>
              </div>
            )}

            {/* Alerts Section */}
            {alerts.length > 0 && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Active Alerts ({alerts.length})</h2>
                <div style={styles.alertsList}>
                  {alerts.slice(0, 5).map(alert => (
                    <div key={alert.id} style={{
                      ...styles.alertItem,
                      borderLeftColor: alert.priority === 'HIGH' || alert.priority === 'URGENT' ? '#f44336' : '#ff9800',
                    }}>
                      <div style={styles.alertContent}>
                        <div style={styles.alertTitle}>{alert.title}</div>
                        <div style={styles.alertMessage}>{alert.message}</div>
                        <div style={styles.alertMeta}>
                          {new Date(alert.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        style={styles.resolveBtn}
                        onClick={() => handleResolveAlert(alert.id)}
                      >
                        Resolve
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Low Stock Items */}
            {dashboard.low_stock_items.length > 0 && (
              <div style={styles.section}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Low Stock Items</h2>
                  <button
                    onClick={openRestockModal}
                    style={styles.restockBtn}
                  >
                    Request Restock from Mill
                  </button>
                </div>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Product</th>
                        <th style={styles.th}>Location</th>
                        <th style={styles.th}>Qty</th>
                        <th style={styles.th}>Threshold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.low_stock_items.map(item => (
                        <tr key={item.id} style={styles.tr}>
                          <td style={styles.td}>{item.product_name}</td>
                          <td style={styles.td}>{item.location}</td>
                          <td style={{...styles.td, color: '#f44336', fontWeight: 'bold'}}>{item.quantity}</td>
                          <td style={styles.td}>{item.reorder_threshold}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Recent Activity</h2>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Product</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th}>By</th>
                      <th style={styles.th}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recent_transactions.map(t => (
                      <tr key={t.id} style={styles.tr}>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.typeBadge,
                            backgroundColor: t.type === 'ADD' ? '#e8f5e9' : t.type === 'SALE' ? '#fff3e0' : '#e3f2fd',
                            color: t.type === 'ADD' ? '#2e7d32' : t.type === 'SALE' ? '#ef6c00' : '#1565c0',
                          }}>
                            {t.type_display}
                          </span>
                        </td>
                        <td style={styles.td}>{t.product_name}</td>
                        <td style={styles.td}>
                          {t.quantity_lbs ? `${t.quantity_lbs} lbs` : t.quantity}
                        </td>
                        <td style={styles.td}>{t.user}</td>
                        <td style={styles.td}>
                          {new Date(t.timestamp).toLocaleTimeString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}

        {/* Restock Request Modal */}
        {showRestockModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <div style={styles.modalHeader}>
                <h2 style={{ margin: 0 }}>Request Restock from Mill</h2>
                <button
                  onClick={() => setShowRestockModal(false)}
                  style={styles.closeBtn}
                >
                  &times;
                </button>
              </div>

              {restockSuccess ? (
                <div style={styles.successMessage}>
                  {restockSuccess}
                  <button
                    onClick={() => setShowRestockModal(false)}
                    style={{ ...styles.restockBtn, marginTop: '1rem' }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <p style={{ color: '#666', marginBottom: '1rem' }}>
                    Select items to include in your restock request. The mill team will be notified.
                  </p>

                  {lowStockItems.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666' }}>No low stock items found.</p>
                  ) : (
                    <div style={styles.modalTableContainer}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Select</th>
                            <th style={styles.th}>Product</th>
                            <th style={styles.th}>Current</th>
                            <th style={styles.th}>Order Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lowStockItems.map(item => (
                            <tr key={item.id} style={styles.tr}>
                              <td style={styles.td}>
                                <input
                                  type="checkbox"
                                  checked={selectedItems[item.id] !== undefined}
                                  onChange={() => toggleItem(item.id, item.reorder_quantity)}
                                  style={{ width: '24px', height: '24px', minWidth: '44px', minHeight: '44px', cursor: 'pointer' }}
                                />
                              </td>
                              <td style={styles.td}>
                                {item.product_name}
                                <div style={{ fontSize: '0.75rem', color: '#999' }}>
                                  Deficit: {item.deficit}
                                </div>
                              </td>
                              <td style={{ ...styles.td, color: '#f44336', fontWeight: 'bold' }}>
                                {item.current_quantity}
                              </td>
                              <td style={styles.td}>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={selectedItems[item.id] ?? item.reorder_quantity}
                                  onChange={(e) => updateQuantity(item.id, e.target.value as any)}
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    updateQuantity(item.id, Math.max(1, val));
                                  }}
                                  disabled={selectedItems[item.id] === undefined}
                                  style={{
                                    width: '70px',
                                    padding: '0.5rem',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '16px',
                                    minHeight: '44px',
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div style={styles.modalFooter}>
                    <button
                      onClick={() => setShowRestockModal(false)}
                      style={styles.cancelBtn}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitRestockRequest}
                      disabled={restockLoading || Object.keys(selectedItems).length === 0}
                      style={{
                        ...styles.restockBtn,
                        opacity: restockLoading || Object.keys(selectedItems).length === 0 ? 0.6 : 1,
                      }}
                    >
                      {restockLoading ? 'Sending...' : `Send Request (${Object.keys(selectedItems).length} items)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  welcomeSection: {
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  welcome: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  date: {
    color: '#666',
    margin: '0.25rem 0 0 0',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '8px',
    textAlign: 'center',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  alertCard: {
    backgroundColor: '#fff3e0',
    border: '2px solid #ff9800',
  },
  cardIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  cardLabel: {
    fontSize: '0.875rem',
    color: '#666',
    marginTop: '0.25rem',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#333',
    marginTop: 0,
    marginBottom: '1rem',
  },
  alertsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  alertItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    borderLeft: '4px solid #ff9800',
  },
  alertContent: {
    flex: 1,
  },
  alertTitle: {
    fontWeight: 600,
    color: '#333',
  },
  alertMessage: {
    fontSize: '0.875rem',
    color: '#666',
    marginTop: '0.25rem',
  },
  alertMeta: {
    fontSize: '0.75rem',
    color: '#999',
    marginTop: '0.25rem',
  },
  resolveBtn: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    padding: '0.625rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: '44px',
    minWidth: '44px',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #e0e0e0',
    color: '#666',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.875rem',
  },
  typeBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  restockBtn: {
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    padding: '0.625rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: '44px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '1rem',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.25rem 0.5rem',
  },
  successMessage: {
    textAlign: 'center',
    padding: '2rem',
    color: '#2e7d32',
    fontSize: '1rem',
  },
  modalTableContainer: {
    overflowY: 'auto',
    maxHeight: '400px',
    marginBottom: '1rem',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '1rem',
    marginTop: 'auto',
  },
  cancelBtn: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '0.625rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: '44px',
  },
};

export default StoreDashboard;
